import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Drawer,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  Stack,
  CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { api } from "../api";
import {
  UnidadesApi,
  IClienteUnidade,
  IPlanoBase,
  IFaturaCliente,
  IHardwareDisco,
  ITarefaCliente
} from "../api/unidadesApi";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

export default function GerenciadorClientes() {
  const theme = useTheme();

  // Estados principais
  const [clientes, setClientes] = useState<IClienteUnidade[]>([]);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const [planoBases, setPlanoBases] = useState<IPlanoBase[]>([]);

  // Estado do Drawer de Detalhes
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState<IClienteUnidade | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Estados do Drawer (Dados do Cliente)
  const [hardware, setHardware] = useState<IHardwareDisco[]>([]);
  const [faturas, setFaturas] = useState<IFaturaCliente[]>([]);
  const [tarefas, setTarefas] = useState<ITarefaCliente[]>([]);
  const [loadingDrawer, setLoadingDrawer] = useState(false);

  // Estados do Storage Frio
  const [coldStorageAtivo, setColdStorageAtivo] = useState(false);
  const [coldStorageEndpoint, setColdStorageEndpoint] = useState("");
  const [coldStorageKey, setColdStorageKey] = useState("");
  const [coldStorageSecret, setColdStorageSecret] = useState("");
  const [coldStorageBucket, setColdStorageBucket] = useState("");
  const [coldStorageRegion, setColdStorageRegion] = useState("");
  const [salvandoColdStorage, setSalvandoColdStorage] = useState(false);

  // Estado de download de contrato
  const [baixandoContratoId, setBaixandoContratoId] = useState<string | number | null>(null);

  const baixarContratoCliente = async (clienteID: string | number, nomeCliente?: string) => {
    setBaixandoContratoId(clienteID);
    try {
      const response = await api.get(`/v2/unidades/contratos/pdf/${clienteID}`, {
        responseType: "blob"
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const nomeSanitizado = (nomeCliente || "Cliente").replace(/[^a-zA-Z0-9_-]/g, "_");
      link.setAttribute("download", `Contrato_Backup_Facil_${nomeSanitizado}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Erro ao baixar contrato:", err);
      alert("Não foi possível gerar o contrato em PDF neste momento.");
    } finally {
      setBaixandoContratoId(null);
    }
  };

  // Estado do Modal de Cadastro
  const [modalCadastroOpen, setModalCadastroOpen] = useState(false);
  const [cnpjBusca, setCnpjBusca] = useState("");
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cadastroErro, setCadastroErro] = useState<string | null>(null);

  // Form de Cadastro
  const [formCnpj, setFormCnpj] = useState("");
  const [formRazaoSocial, setFormRazaoSocial] = useState("");
  const [formNomeFantasia, setFormNomeFantasia] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formTelefone, setFormTelefone] = useState("");
  const [formPlanoBaseID, setFormPlanoBaseID] = useState("");
  const [formTipoPlano, setFormTipoPlano] = useState<"mensal" | "anual">("mensal");
  const [formValorVenda, setFormValorVenda] = useState("");
  
  // Endereço do Cadastro
  const [formCep, setFormCep] = useState("");
  const [formLogradouro, setFormLogradouro] = useState("");
  const [formNumero, setFormNumero] = useState("");
  const [formComplemento, setFormComplemento] = useState("");
  const [formMunicipioID, setFormMunicipioID] = useState("");

  // Estado de Sucesso de Cadastro (Exibe credenciais)
  const [credenciaisCriadas, setCredenciaisCriadas] = useState<{ email: string; senhaOriginal: string } | null>(null);
  const [modalSucessoOpen, setModalSucessoOpen] = useState(false);

  // Estados de Nova Tarefa Remota
  const [modalNovaTarefaOpen, setModalNovaTarefaOpen] = useState(false);
  const [tarefaNome, setTarefaNome] = useState("");
  const [tarefaTipo, setTarefaTipo] = useState("Arquivos"); // Arquivos, SQLServer, Postgres, Bat
  const [tarefaTipoAgendamento, setTarefaTipoAgendamento] = useState("Diariamente");
  const [tarefaHoraExecucao, setTarefaHoraExecucao] = useState("22:00");
  
  // Campos extras de tarefa
  const [origemArquivos, setOrigemArquivos] = useState("");
  const [destinoArquivos, setDestinoArquivos] = useState("");
  const [bancoServidor, setBancoServidor] = useState("");
  const [bancoPorta, setBancoPorta] = useState("");
  const [bancoNome, setBancoNome] = useState("");
  const [bancoUsuario, setBancoUsuario] = useState("");
  const [bancoSenha, setBancoSenha] = useState("");
  const [comandoBat, setComandoBat] = useState("");

  useEffect(() => {
    carregarClientes();
    carregarPlanosBase();
  }, []);

  const carregarClientes = async () => {
    try {
      setLoading(true);
      const data = await UnidadesApi.getClientes();
      setClientes(data);
    } catch (e) {
      console.error("Erro ao carregar clientes", e);
    } finally {
      setLoading(false);
    }
  };

  const carregarPlanosBase = async () => {
    try {
      const data = await UnidadesApi.getPlanosBase();
      setPlanoBases(data);
      if (data.length > 0) {
        setFormPlanoBaseID(data[0].id);
        setFormValorVenda(String(data[0].precoBaseMensal));
      }
    } catch (e) {
      console.error("Erro ao carregar planos base", e);
    }
  };

  // Carregar dados específicos ao selecionar cliente e trocar de aba
  useEffect(() => {
    if (!clienteSelecionado) return;

    if (tabValue === 0) {
      // Aba de Histórico/Saúde: tarefas também fornecem dados de saúde recentes
      carregarTarefas(clienteSelecionado.id);
    } else if (tabValue === 1) {
      carregarTarefas(clienteSelecionado.id);
    } else if (tabValue === 2) {
      carregarHardware(clienteSelecionado.id);
    } else if (tabValue === 3) {
      carregarFaturas(clienteSelecionado.id);
    }
  }, [clienteSelecionado, tabValue]);

  // Sincronizar estados do Storage Frio ao selecionar cliente
  useEffect(() => {
    if (clienteSelecionado) {
      setColdStorageAtivo(clienteSelecionado.coldStorageAtivo || false);
      setColdStorageEndpoint(clienteSelecionado.coldStorageEndpoint || "");
      setColdStorageKey(clienteSelecionado.coldStorageKey || "");
      setColdStorageSecret(clienteSelecionado.coldStorageSecret || "");
      setColdStorageBucket(clienteSelecionado.coldStorageBucket || "");
      setColdStorageRegion(clienteSelecionado.coldStorageRegion || "");
    } else {
      setColdStorageAtivo(false);
      setColdStorageEndpoint("");
      setColdStorageKey("");
      setColdStorageSecret("");
      setColdStorageBucket("");
      setColdStorageRegion("");
    }
  }, [clienteSelecionado]);

  const handleSalvarColdStorage = async () => {
    if (!clienteSelecionado) return;
    try {
      setSalvandoColdStorage(true);
      await UnidadesApi.salvarColdStorage(clienteSelecionado.id, {
        coldStorageAtivo,
        coldStorageEndpoint,
        coldStorageKey,
        coldStorageSecret,
        coldStorageBucket,
        coldStorageRegion
      });
      const updatedClient = {
        ...clienteSelecionado,
        coldStorageAtivo,
        coldStorageEndpoint,
        coldStorageKey,
        coldStorageSecret,
        coldStorageBucket,
        coldStorageRegion
      };
      setClienteSelecionado(updatedClient);
      setClientes(prev => prev.map(c => c.id === clienteSelecionado.id ? updatedClient : c));
      alert("Configurações de Storage Frio salvas com sucesso!");
    } catch (e: any) {
      console.error(e);
      alert("Erro ao salvar configurações de Storage Frio: " + (e.response?.data?.mensagem || e.message));
    } finally {
      setSalvandoColdStorage(false);
    }
  };

  const carregarHardware = async (clienteID: string) => {
    try {
      setLoadingDrawer(true);
      const data = await UnidadesApi.getHardwareCliente(clienteID);
      setHardware(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDrawer(false);
    }
  };

  const carregarFaturas = async (clienteID: string) => {
    try {
      setLoadingDrawer(true);
      const data = await UnidadesApi.getFaturasCliente(clienteID);
      setFaturas(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDrawer(false);
    }
  };

  const carregarTarefas = async (clienteID: string) => {
    try {
      setLoadingDrawer(true);
      const data = await UnidadesApi.getTarefasCliente(clienteID);
      setTarefas(data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDrawer(false);
    }
  };

  const handlePagarFatura = async (faturaID: string) => {
    if (!clienteSelecionado) return;
    try {
      setLoadingDrawer(true);
      await UnidadesApi.pagarFaturaSimulada(faturaID);
      await carregarFaturas(clienteSelecionado.id);
      await carregarClientes(); // Atualiza faturamento do dashboard se necessário
    } catch (e) {
      console.error(e);
      alert("Erro ao simular pagamento de fatura.");
    } finally {
      setLoadingDrawer(false);
    }
  };

  const handleAlterarStatusTarefa = async (tarefaID: string, statusAtual: string) => {
    if (!clienteSelecionado) return;
    try {
      setLoadingDrawer(true);
      const novoStatus = statusAtual === "ativa" ? "inativa" : "ativa";
      await UnidadesApi.alterarStatusTarefaCliente(clienteSelecionado.id, tarefaID, novoStatus);
      await carregarTarefas(clienteSelecionado.id);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDrawer(false);
    }
  };

  // Buscar CNPJ via ReceitaWS/API do backend
  const handleBuscarCnpj = async () => {
    if (!cnpjBusca) return;
    try {
      setCnpjLoading(true);
      setCadastroErro(null);
      
      const response = await api.get(`/cnpj/${cnpjBusca.replace(/\D/g, "")}`);
      const info = response.data;
      
      setFormCnpj(info.pessoaJuridica.cnpj);
      setFormRazaoSocial(info.nome);
      setFormNomeFantasia(info.pessoaJuridica.nomeFantasia);
      setFormEmail(info.emails && info.emails.length > 0 ? info.emails[0].email : "");
      setFormTelefone(info.telefones && info.telefones.length > 0 ? `(${info.telefones[0].ddd}) ${info.telefones[0].numero}` : "");
      
      if (info.endereco) {
        setFormCep(String(info.endereco.cep));
        setFormLogradouro(info.endereco.logradouro);
        setFormNumero(info.endereco.numero);
        setFormComplemento(info.endereco.complemento);
        setFormMunicipioID(info.endereco.municipioID);
        // O nome do município podemos inferir ou buscar se necessário, mas o id é o importante para salvar
      }
    } catch (e: any) {
      console.error(e);
      setCadastroErro("Erro ao consultar CNPJ. Preencha os campos manualmente.");
    } finally {
      setCnpjLoading(false);
    }
  };

  // Enviar cadastro simplificado do cliente
  const handleCadastrarCliente = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setCadastroErro(null);

      const payload = {
        cnpj: formCnpj,
        razaoSocial: formRazaoSocial,
        nomeFantasia: formNomeFantasia,
        email: formEmail,
        telefone: formTelefone,
        planoBaseID: formPlanoBaseID,
        tipoPlano: formTipoPlano,
        valorVenda: Number(formValorVenda),
        endereco: {
          cep: formCep,
          logradouro: formLogradouro,
          numero: formNumero,
          complemento: formComplemento,
          municipioID: Number(formMunicipioID || 1) // Fallback seguro
        }
      };

      const result = await UnidadesApi.cadastrarClienteSimplificado(payload);
      
      // Exibir popup de credenciais criadas
      setCredenciaisCriadas(result.credenciais);
      setModalCadastroOpen(false);
      setModalSucessoOpen(true);
      
      // Limpar form
      resetForm();
      
      // Recarregar listagem
      await carregarClientes();
    } catch (e: any) {
      console.error(e);
      setCadastroErro(e.response?.data?.mensagem || "Erro ao realizar o cadastro do cliente.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormCnpj("");
    setFormRazaoSocial("");
    setFormNomeFantasia("");
    setFormEmail("");
    setFormTelefone("");
    setFormCep("");
    setFormLogradouro("");
    setFormNumero("");
    setFormComplemento("");
    setFormMunicipioID("");
    setCnpjBusca("");
    setCadastroErro(null);
  };

  // Enviar criação de tarefa remota
  const handleCriarTarefaRemota = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clienteSelecionado) return;
    try {
      setLoadingDrawer(true);
      
      let arquivos = null;
      let batch = null;
      let configBancoDados = null;

      if (tarefaTipo === "Arquivos") {
        arquivos = {
          pastaOrigem: origemArquivos,
          incluirSubPastas: true,
          pastaDestino: destinoArquivos
        };
      } else if (tarefaTipo === "Bat") {
        batch = {
          comando: comandoBat,
          pastaDestino: destinoArquivos
        };
      } else {
        configBancoDados = {
          descricao: tarefaNome,
          servidor: bancoServidor,
          porta: Number(bancoPorta || 1433),
          bancoDados: bancoNome,
          usuario: bancoUsuario,
          senha: bancoSenha,
          tipo: "Completo"
        };
      }

      const payload = {
        nome: tarefaNome,
        tipo: tarefaTipo,
        grupo: { nome: "Remoto" },
        dataInicio: new Date().toISOString().split('T')[0],
        agendamento: {
          tipo: tarefaTipoAgendamento,
          horaExecucao: tarefaHoraExecucao
        },
        arquivos,
        batch,
        configBancoDados
      };

      await UnidadesApi.criarTarefaCliente(clienteSelecionado.id, payload);
      setModalNovaTarefaOpen(false);
      await carregarTarefas(clienteSelecionado.id);
      
      // Reset tarefa form
      setTarefaNome("");
      setOrigemArquivos("");
      setDestinoArquivos("");
      setBancoServidor("");
      setBancoPorta("");
      setBancoNome("");
      setBancoUsuario("");
      setBancoSenha("");
      setComandoBat("");
    } catch (e: any) {
      console.error(e);
      alert("Erro ao criar tarefa de backup.");
    } finally {
      setLoadingDrawer(false);
    }
  };

  const handleCopiarCredenciais = () => {
    if (!credenciaisCriadas) return;
    const texto = `E-mail de Login: ${credenciaisCriadas.email}\nSenha de Acesso: ${credenciaisCriadas.senhaOriginal}`;
    navigator.clipboard.writeText(texto);
    alert("Credenciais copiadas para a área de transferência!");
  };

  // Filtrar clientes
  const clientesFiltrados = clientes.filter(c =>
    c.nome.toLowerCase().includes(filtro.toLowerCase()) ||
    c.email.toLowerCase().includes(filtro.toLowerCase())
  );

  // Calcular comissão instantânea no formulário de cadastro
  const planoSelecionado = planoBases.find(p => p.id === formPlanoBaseID);
  const precoBase = planoSelecionado
    ? (formTipoPlano === "anual" ? Number(planoSelecionado.precoBaseAnual) : Number(planoSelecionado.precoBaseMensal))
    : 0;
  const valorVendaNum = Number(formValorVenda || 0);
  const comissaoEstimada = valorVendaNum > precoBase ? (valorVendaNum - precoBase) * 0.8 : 0;

  return (
    <Box sx={{ flexGrow: 1, p: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
          Clientes da Carteira
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setModalCadastroOpen(true)}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
        >
          Novo Cliente
        </Button>
      </Box>

      {/* Barra de Filtros */}
      <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por nome ou e-mail..."
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          sx={{ width: 350 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Listagem em Tabela */}
      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}>
                <TableCell sx={{ fontWeight: 700 }}>Nome / Empresa</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Plano Contratado</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Armazenamento Usado</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Último Backup</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientesFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Nenhum cliente encontrado nesta carteira.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clientesFiltrados.map((cliente) => (
                  <TableRow key={cliente.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{cliente.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">{cliente.email}</Typography>
                    </TableCell>
                    <TableCell>
                      {cliente.plano ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {cliente.plano.nomeBase} ({cliente.plano.tipo === "anual" ? "Anual" : "Mensal"})
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Vendido por: R$ {Number(cliente.plano.valorVenda).toFixed(2)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.disabled">Sem plano ativo</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: "100%", mr: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {cliente.usadoGB} GB de {cliente.capacidadeArmazenamentoGB} GB
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cliente.capacidadeArmazenamentoGB > 0
                              ? Math.round((cliente.usadoGB / cliente.capacidadeArmazenamentoGB) * 100)
                              : 0}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={
                            cliente.capacidadeArmazenamentoGB > 0
                              ? Math.min((cliente.usadoGB / cliente.capacidadeArmazenamentoGB) * 100, 100)
                              : 0
                          }
                          color={
                            (cliente.capacidadeArmazenamentoGB > 0 && (cliente.usadoGB / cliente.capacidadeArmazenamentoGB) > 0.9)
                              ? "error"
                              : (cliente.capacidadeArmazenamentoGB > 0 && (cliente.usadoGB / cliente.capacidadeArmazenamentoGB) > 0.7)
                                ? "warning"
                                : "primary"
                          }
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {cliente.ultimoBackup ? (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {new Date(cliente.ultimoBackup.dataHora).toLocaleDateString("pt-BR")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Tamanho: {cliente.ultimoBackup.tamanhoGB} GB
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.disabled">Nenhum backup</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<SettingsIcon />}
                          onClick={() => {
                            setClienteSelecionado(cliente);
                            setTabValue(0);
                            setDrawerOpen(true);
                          }}
                          sx={{ textTransform: "none", borderRadius: "6px" }}
                        >
                          Gerenciar
                        </Button>
                        <Tooltip title="Baixar Contrato Oficial (PDF)">
                          <span>
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => baixarContratoCliente(cliente.id, cliente.nome)}
                              disabled={baixandoContratoId === cliente.id}
                              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "6px" }}
                            >
                              {baixandoContratoId === cliente.id ? <CircularProgress size={16} /> : <PictureAsPdfIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Drawer Lateral de Detalhes e Operação do Cliente */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100%", md: 600 }, p: 0 }
        }}
      >
        {clienteSelecionado && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Header do Drawer */}
            <Box sx={{ p: 3, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {clienteSelecionado.nome}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Código do Cliente: #{clienteSelecionado.codigo}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={baixandoContratoId === clienteSelecionado.id ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                  onClick={() => baixarContratoCliente(clienteSelecionado.id, clienteSelecionado.nome)}
                  disabled={baixandoContratoId === clienteSelecionado.id}
                  sx={{ textTransform: "none", borderRadius: "6px" }}
                >
                  {baixandoContratoId === clienteSelecionado.id ? "Gerando..." : "Contrato PDF"}
                </Button>
                <IconButton onClick={() => setDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} variant="fullWidth">
                <Tab label="Saúde" />
                <Tab label="Tarefas" />
                <Tab label="Discos" />
                <Tab label="Faturamento" />
                <Tab label="Storage Frio" />
              </Tabs>
            </Box>

            {/* Loading Drawer */}
            {loadingDrawer && <LinearProgress />}

            {/* Conteúdo das Tabs */}
            <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
              
              {/* Aba 0: Saúde e Alertas */}
              <CustomTabPanel value={tabValue} index={0}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Saúde do Backup Recente</Typography>
                {tarefas.length === 0 ? (
                  <Alert severity="info">Nenhuma tarefa de backup configurada para este cliente.</Alert>
                ) : (
                  <List>
                    {tarefas.map(t => {
                      const statusSucesso = t.ultimaExecucao?.status.toLowerCase() === "concluido" || t.ultimaExecucao?.status.toLowerCase() === "concluído";
                      return (
                        <ListItem key={t.id} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 0 }}>
                          <ListItemIcon>
                            {statusSucesso ? <CheckCircleIcon color="success" /> : <WarningIcon color="error" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={t.nome}
                            secondary={
                              <>
                                <Typography variant="caption" display="block">
                                  Tipo: {t.tipo} • Agendamento: {t.tipoAgendamento}
                                </Typography>
                                <Typography variant="caption" display="block" color={statusSucesso ? "success.main" : "error.main"}>
                                  Última Execução: {t.ultimaExecucao ? `${new Date(t.ultimaExecucao.dataHora).toLocaleString("pt-BR")} (${t.ultimaExecucao.status})` : "Nunca executada"}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CustomTabPanel>

              {/* Aba 1: Gestão de Tarefas Remotas */}
              <CustomTabPanel value={tabValue} index={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Tarefas Configuradas</Typography>
                  <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setModalNovaTarefaOpen(true)}>
                    Nova Tarefa
                  </Button>
                </Box>
                {tarefas.length === 0 ? (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Nenhuma tarefa remota cadastrada.</Typography>
                ) : (
                  <List>
                    {tarefas.map(t => (
                      <ListItem
                        key={t.id}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: "8px",
                          mb: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <ListItemText
                          primary={<Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>{t.nome}</Typography>}
                          secondary={
                            <>
                              <Typography variant="caption" display="block">Tipo: {t.tipo} • Hora: {t.horaExecucao}</Typography>
                              <Typography variant="caption" color="text.secondary">Status: {t.status}</Typography>
                            </>
                          }
                        />
                        <Box>
                          <Tooltip title={t.status === "ativa" ? "Pausar tarefa" : "Ativar tarefa"}>
                            <IconButton onClick={() => handleAlterarStatusTarefa(t.id, t.status)}>
                              {t.status === "ativa" ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CustomTabPanel>

              {/* Aba 2: Saúde do Hardware */}
              <CustomTabPanel value={tabValue} index={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Saúde das Unidades de Armazenamento</Typography>
                {hardware.length === 0 ? (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Sem informações de hardware disponíveis.</Typography>
                ) : (
                  <Grid container spacing={2}>
                    {hardware.map(disco => (
                      <Grid item xs={12} key={disco.id}>
                        <Card sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
                          <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Unidade {disco.unidade}:
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {disco.percentualUso}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={disco.percentualUso}
                              color={disco.percentualUso > 90 ? "error" : disco.percentualUso > 75 ? "warning" : "success"}
                              sx={{ height: 8, borderRadius: 4, mb: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Espaço Livre: {Math.round(disco.capacidadeLivre / 1024 * 10) / 10} GB de {Math.round(disco.capacidadeTotal / 1024 * 10) / 10} GB total
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CustomTabPanel>

              {/* Aba 3: Financeiro / Faturas */}
              <CustomTabPanel value={tabValue} index={3}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Mensalidades e Comissões</Typography>
                {faturas.length === 0 ? (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Nenhuma fatura encontrada.</Typography>
                ) : (
                  <List>
                    {faturas.map(fat => (
                      <ListItem
                        key={fat.id}
                        sx={{
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          '&:last-child': { borderBottom: 'none' },
                          px: 0,
                          py: 1.5,
                          display: "flex",
                          justifyContent: "space-between"
                        }}
                      >
                        <ListItemText
                          primary={`Competência: ${fat.competencia}`}
                          secondary={
                            <>
                              <Typography variant="caption" display="block">Vence em: {new Date(fat.dataVencimento).toLocaleDateString("pt-BR")}</Typography>
                              <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                                Valor Cobrado: R$ {Number(fat.valorVenda).toFixed(2)} (Sua Comissão: R$ {Number(fat.valorComissao).toFixed(2)})
                              </Typography>
                            </>
                          }
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={fat.status === "paga" ? "Paga" : "Pendente"}
                            color={fat.status === "paga" ? "success" : "warning"}
                            size="small"
                          />
                          {fat.status !== "paga" && (
                            <Button size="small" variant="contained" color="secondary" onClick={() => handlePagarFatura(fat.id)}>
                              Simular Pago
                            </Button>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
               </CustomTabPanel>

              {/* Aba 4: Storage Frio */}
              <CustomTabPanel value={tabValue} index={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Configuração de Storage Frio (AWS S3 / Backblaze B2)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Habilite e configure a replicação redundante em storages compatíveis com S3 para este cliente.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={coldStorageAtivo}
                        onChange={(e) => setColdStorageAtivo(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Ativar Storage Frio</Typography>
                        <Typography variant="caption" color="text.secondary">Habilita o espelhamento de novos arquivos concluídos.</Typography>
                      </Box>
                    }
                  />

                  {coldStorageAtivo && (
                    <>
                      <TextField
                        label="Endpoint do S3"
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="Ex: s3.us-east-1.amazonaws.com ou s3.us-west-004.backblazeb2.com"
                        value={coldStorageEndpoint}
                        onChange={(e) => setColdStorageEndpoint(e.target.value)}
                      />

                      <TextField
                        label="Nome do Bucket"
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="Ex: backup-facil-cliente-bucket"
                        value={coldStorageBucket}
                        onChange={(e) => setColdStorageBucket(e.target.value)}
                      />

                      <TextField
                        label="Região (Opcional)"
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="Ex: us-east-1"
                        value={coldStorageRegion}
                        onChange={(e) => setColdStorageRegion(e.target.value)}
                      />

                      <TextField
                        label="Chave de Acesso (Access Key ID)"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={coldStorageKey}
                        onChange={(e) => setColdStorageKey(e.target.value)}
                      />

                      <TextField
                        label="Chave Secreta (Secret Access Key)"
                        variant="outlined"
                        type="password"
                        fullWidth
                        size="small"
                        value={coldStorageSecret}
                        onChange={(e) => setColdStorageSecret(e.target.value)}
                      />
                    </>
                  )}

                  <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      onClick={handleSalvarColdStorage}
                      disabled={salvandoColdStorage}
                      sx={{ textTransform: "none", borderRadius: "8px" }}
                    >
                      {salvandoColdStorage ? "Salvando..." : "Salvar Configurações"}
                    </Button>
                  </Box>
                </Box>
              </CustomTabPanel>

            </Box>
          </Box>
        )}
      </Drawer>

      {/* Modal de Cadastro de Cliente Simplificado */}
      <Dialog open={modalCadastroOpen} onClose={() => setModalCadastroOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cadastrar Novo Cliente</DialogTitle>
        <DialogContent>
          {cadastroErro && <Alert severity="error" sx={{ mb: 2 }}>{cadastroErro}</Alert>}

          {/* Consulta Rápida CNPJ */}
          <Box sx={{ display: "flex", gap: 1, mb: 3, mt: 1 }}>
            <TextField
              size="small"
              label="CNPJ para Consulta Rápida"
              placeholder="Apenas números..."
              value={cnpjBusca}
              onChange={(e) => setCnpjBusca(e.target.value)}
              fullWidth
            />
            <Button variant="outlined" startIcon={<SearchIcon />} onClick={handleBuscarCnpj} disabled={cnpjLoading}>
              {cnpjLoading ? "Consultando..." : "Consultar"}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <form id="form-cadastro-cliente" onSubmit={handleCadastrarCliente}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  size="small"
                  label="CNPJ"
                  value={formCnpj}
                  onChange={(e) => setFormCnpj(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  size="small"
                  label="Razão Social"
                  value={formRazaoSocial}
                  onChange={(e) => setFormRazaoSocial(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Nome Fantasia"
                  value={formNomeFantasia}
                  onChange={(e) => setFormNomeFantasia(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  size="small"
                  label="E-mail de Login/Financeiro"
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Telefone de Contato"
                  value={formTelefone}
                  onChange={(e) => setFormTelefone(e.target.value)}
                  fullWidth
                />
              </Grid>

              {/* Endereço */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  size="small"
                  label="CEP"
                  value={formCep}
                  onChange={(e) => setFormCep(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                <TextField
                  required
                  size="small"
                  label="Logradouro"
                  value={formLogradouro}
                  onChange={(e) => setFormLogradouro(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  required
                  size="small"
                  label="Número"
                  value={formNumero}
                  onChange={(e) => setFormNumero(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Complemento"
                  value={formComplemento}
                  onChange={(e) => setFormComplemento(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  size="small"
                  label="Código do Município (IBGE)"
                  value={formMunicipioID}
                  onChange={(e) => setFormMunicipioID(e.target.value)}
                  fullWidth
                />
              </Grid>

              {/* Plano e Valores */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Plano Contratado do Cliente</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  label="Selecione o Plano"
                  value={formPlanoBaseID}
                  onChange={(e) => {
                    setFormPlanoBaseID(e.target.value);
                    const selected = planoBases.find(p => p.id === e.target.value);
                    if (selected) {
                      setFormValorVenda(String(formTipoPlano === "anual" ? selected.precoBaseAnual : selected.precoBaseMensal));
                    }
                  }}
                  fullWidth
                >
                  {planoBases.map((plano) => (
                    <MenuItem key={plano.id} value={plano.id}>
                      {plano.nome} ({plano.capacidadeGb} GB)
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  label="Tipo de Faturamento"
                  value={formTipoPlano}
                  onChange={(e) => {
                    const val = e.target.value as "mensal" | "anual";
                    setFormTipoPlano(val);
                    const selected = planoBases.find(p => p.id === formPlanoBaseID);
                    if (selected) {
                      setFormValorVenda(String(val === "anual" ? selected.precoBaseAnual : selected.precoBaseMensal));
                    }
                  }}
                  fullWidth
                >
                  <MenuItem value="mensal">Mensal</MenuItem>
                  <MenuItem value="anual">Anual</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  size="small"
                  label="Preço de Venda cobrado do Cliente (R$)"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={formValorVenda}
                  onChange={(e) => setFormValorVenda(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, bgcolor: theme.palette.mode === 'dark' ? "rgba(16, 185, 129, 0.05)" : "rgba(16, 185, 129, 0.05)", border: "1px dashed rgba(16, 185, 129, 0.3)", borderRadius: "8px" }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    SUA COMISSÃO ESTIMADA:
                  </Typography>
                  <Typography variant="subtitle1" color="success.main" sx={{ fontWeight: 700 }}>
                    R$ {comissaoEstimada.toFixed(2)} / {formTipoPlano === "anual" ? "ano" : "mês"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalCadastroOpen(false)} color="inherit">Cancelar</Button>
          <Button form="form-cadastro-cliente" type="submit" variant="contained" color="primary">
            Cadastrar Cliente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Sucesso com credenciais geradas */}
      <Dialog open={modalSucessoOpen} onClose={() => setModalSucessoOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon color="success" /> Cliente Cadastrado!
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            O cliente foi cadastrado na sua carteira de revenda. Copie as credenciais abaixo e repasse para o cliente poder instalar e autenticar o Agente de Backup:
          </Typography>

          {credenciaisCriadas && (
            <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", borderRadius: "8px", border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                Login de Acesso:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace", color: "primary.main", mb: 1.5 }}>
                {credenciaisCriadas.email}
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                Senha Gerada Temporária:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace", color: "primary.main" }}>
                {credenciaisCriadas.senhaOriginal}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopiarCredenciais} variant="outlined">
            Copiar Credenciais
          </Button>
          <Button onClick={() => setModalSucessoOpen(false)} variant="contained" color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Criação de Nova Tarefa Remota */}
      <Dialog open={modalNovaTarefaOpen} onClose={() => setModalNovaTarefaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Criar Nova Tarefa de Backup Remota</DialogTitle>
        <DialogContent>
          <form id="form-criar-tarefa" onSubmit={handleCriarTarefaRemota} style={{ marginTop: "10px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  size="small"
                  label="Nome da Tarefa"
                  placeholder="Ex: Backup do Sistema Financeiro"
                  value={tarefaNome}
                  onChange={(e) => setTarefaNome(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  label="Tipo de Backup"
                  value={tarefaTipo}
                  onChange={(e) => setTarefaTipo(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="Arquivos">Arquivos e Pastas locais</MenuItem>
                  <MenuItem value="SQLServer">Microsoft SQL Server</MenuItem>
                  <MenuItem value="Postgres">PostgreSQL</MenuItem>
                  <MenuItem value="Bat">Script em Lote (.bat)</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  label="Frequência"
                  value={tarefaTipoAgendamento}
                  onChange={(e) => setTarefaTipoAgendamento(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="Diariamente">Diariamente</MenuItem>
                  <MenuItem value="AoInicializar">Ao ligar o computador (Modo Eco)</MenuItem>
                  <MenuItem value="Semanalmente">Semanalmente</MenuItem>
                  <MenuItem value="Mensalmente">Mensalmente</MenuItem>
                  <MenuItem value="UmaVez">Uma única vez</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  size="small"
                  label="Horário de Execução"
                  type="time"
                  value={tarefaHoraExecucao}
                  onChange={(e) => setTarefaHoraExecucao(e.target.value)}
                  fullWidth
                />
              </Grid>

              {/* Formulário Condicional baseada no Tipo de Backup */}
              {tarefaTipo === "Arquivos" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      required
                      size="small"
                      label="Caminho da Pasta Origem"
                      placeholder="Ex: C:\Sistemas\Dados"
                      value={origemArquivos}
                      onChange={(e) => setOrigemArquivos(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      size="small"
                      label="Caminho do Destino Relativo"
                      placeholder="Ex: /Sistemas/Dados"
                      value={destinoArquivos}
                      onChange={(e) => setDestinoArquivos(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              {tarefaTipo === "Bat" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      required
                      size="small"
                      label="Linha de Comando (.bat)"
                      placeholder="Ex: backup_db.bat"
                      value={comandoBat}
                      onChange={(e) => setComandoBat(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      size="small"
                      label="Caminho do Destino Relativo"
                      placeholder="Ex: /Sistemas/Scripts"
                      value={destinoArquivos}
                      onChange={(e) => setDestinoArquivos(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              {(tarefaTipo === "SQLServer" || tarefaTipo === "Postgres") && (
                <>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      required
                      size="small"
                      label="IP ou Servidor do Banco"
                      placeholder="Ex: localhost"
                      value={bancoServidor}
                      onChange={(e) => setBancoServidor(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      size="small"
                      label="Porta"
                      placeholder={tarefaTipo === "SQLServer" ? "1433" : "5432"}
                      value={bancoPorta}
                      onChange={(e) => setBancoPorta(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      size="small"
                      label="Nome do Banco de Dados"
                      value={bancoNome}
                      onChange={(e) => setBancoNome(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      size="small"
                      label="Usuário de Conexão"
                      value={bancoUsuario}
                      onChange={(e) => setBancoUsuario(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      size="small"
                      label="Senha de Conexão"
                      type="password"
                      value={bancoSenha}
                      onChange={(e) => setBancoSenha(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalNovaTarefaOpen(false)} color="inherit">Cancelar</Button>
          <Button form="form-criar-tarefa" type="submit" variant="contained" color="primary">
            Salvar Tarefa
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
